// @ts-check
// Diálogos da retenção (contrato 10 de atendimento, seção 24.5): "Marcar risco", "Abrir caso" e "Dispensar", abertos pela
// seção da família, pela tela Casos de retenção e pelos comandos da ficha. Um pedido por vez num sinal; quem hospeda é a
// primeira seção ou tela da retenção montada (RetentionDialogHost).
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import { Modal } from '../../ui/modal.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { showServiceError } from '../service-desk/service-errors.js';
import { dismissRetentionSignal, markHouseholdRisk, openRetentionCase } from '../../data/retention.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {{ kind: 'mark', householdId: string, householdName: string, onDone?: () => void }
 *   | { kind: 'open', householdId: string, householdName: string, onDone?: (ticket: Record<string, any>) => void }
 *   | { kind: 'dismiss', householdId: string, signalId: string, signalLabel: string, onDone?: () => void }} RetentionDialogRequest
 */

/** @type {import('@preact/signals').Signal<RetentionDialogRequest | null>} */
export const retentionDialogState = signal(null);

/** @param {RetentionDialogRequest} request */
export function openRetentionDialog(request) {
  retentionDialogState.value = request;
}

/** @param {{ request: RetentionDialogRequest, onClose: () => void }} props */
function RetentionDialog({ request, onClose }) {
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isNoteRequired = request.kind === 'mark';
  const submit = async () => {
    setIsSaving(true);
    try {
      if (request.kind === 'mark') {
        await markHouseholdRisk(request.householdId, note.trim());
        showSuccessSnackbar(t('retention.dialogs.marked'));
        onClose();
        request.onDone?.();
      } else if (request.kind === 'open') {
        const ticket = await openRetentionCase(request.householdId, note.trim());
        showSuccessSnackbar(t('retention.dialogs.opened', { protocol: ticket?.protocol ?? '' }));
        onClose();
        request.onDone?.(ticket);
      } else {
        await dismissRetentionSignal(request.householdId, request.signalId, note.trim());
        showSuccessSnackbar(t('retention.dialogs.dismissed'));
        onClose();
        request.onDone?.();
      }
    } catch (error) {
      showServiceError(error);
    } finally {
      setIsSaving(false);
    }
  };
  const texts = {
    mark: { title: t('retention.dialogs.markTitle'), subtitle: t('retention.dialogs.markSubtitle', { family: request.kind === 'mark' ? request.householdName : '' }), confirm: t('retention.dialogs.markConfirm') },
    open: { title: t('retention.dialogs.openTitle'), subtitle: t('retention.dialogs.openSubtitle', { family: request.kind === 'open' ? request.householdName : '' }), confirm: t('retention.dialogs.openConfirm') },
    dismiss: { title: t('retention.dialogs.dismissTitle'), subtitle: t('retention.dialogs.dismissSubtitle', { signal: request.kind === 'dismiss' ? request.signalLabel : '' }), confirm: t('retention.dialogs.dismissConfirm') },
  }[request.kind];
  return html`<${Modal} title=${texts.title} subtitle=${texts.subtitle} onClose=${onClose} onConfirm=${submit} confirmLabel=${texts.confirm}
    confirmAccent=${request.kind === 'dismiss' ? 'danger' : 'blue'} isConfirmDisabled=${isNoteRequired && note.trim() === ''} isLoading=${isSaving}>
    <label class="retention-dialog-field">
      <span class="retention-field-label">${isNoteRequired ? t('retention.dialogs.note') : t('retention.dialogs.noteOptional')}</span>
      <textarea class="retention-textarea" rows="3" maxlength="500" value=${note} placeholder=${t('retention.dialogs.notePlaceholder')}
        data-testid="retention-dialog-note" onInput=${(/** @type {any} */ event) => setNote(event.currentTarget.value)}></textarea>
    </label>
  <//>`;
}

// quem hospeda: a primeira seção ou tela montada que continua na tela (a ficha e o painel lateral podem estar juntos)
const hostOwnersState = signal(/** @type {number[]} */ ([]));
let hostSequence = 0;

/** Hospeda o diálogo pedido; montado pela seção da família e pela tela da retenção. */
export function RetentionDialogHost() {
  const [ownerId] = useState(() => (hostSequence += 1));
  useEffect(() => {
    hostOwnersState.value = [...hostOwnersState.value, ownerId];
    return () => {
      hostOwnersState.value = hostOwnersState.value.filter((id) => id !== ownerId);
    };
  }, [ownerId]);
  const request = retentionDialogState.value;
  if (!request || hostOwnersState.value[0] !== ownerId) return null;
  return html`<${RetentionDialog} key=${`${request.kind}:${request.householdId}`} request=${request} onClose=${() => (retentionDialogState.value = null)} />`;
}
